import { Component, ElementRef, Inject, OnDestroy, Renderer2 } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs/internal/Subscription';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/operators';
import { SystemPreferences, SYSTEM_PREFERENCES } from './common/system-preferences';
import './extensions/application-extensions-index';
import { ColorHerlper } from './helpers/color.helper';
import { Task } from './helpers/task';
import { AuthService } from "./services/auth.service";
import { LoginApiService } from './services/system/login-api.service';
import { TcmApplicationManagerService } from './internals/tcm-application-manager.service';
import { SetupTranslationSystemService } from './services/system/setup-translation-system.service';
import { ErrorService } from './services/system/error.service';
import { TcmModalService } from './modules/tcm-common/services/tcm-modal.service';
import { ApiHttpClient, BroadcastService } from './modules/tcm-core.module';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnDestroy {

  get currentUser() {
    return this.authService.currentUserValue;
  }


  isLoading = false;

  readonly calculateBannerTextColorFunc = (backgroundColor: string) => {    
    return ColorHerlper.calculateTextColorByBackground(backgroundColor);
  };

  private userInteractionSubscription: Subscription;
  private navigationSubscription: Subscription;

  private ngUnsubscribe = new Subject();

  constructor(
    public elementRef: ElementRef,
    @Inject(SYSTEM_PREFERENCES) public sysPref: SystemPreferences,
    private http: ApiHttpClient,
    private toastService: ToastrService,
    private router: Router,
    private readonly authService: AuthService,
    private readonly loginService: LoginApiService,
    private readonly appManagerService: TcmApplicationManagerService,
    private readonly setupTranslationSystemService: SetupTranslationSystemService,
    private renderer: Renderer2,
    private errorService: ErrorService,
    private modalService: TcmModalService,
    private broadcastService: BroadcastService) {

      this.setupTranslationSystemService.translationMode$.subscribe(result => {
        if (this.renderer) {
          if (result) {
            this.renderer.addClass(document.body, 'translation-mode');
         } else {
            this.renderer.removeClass(document.body, 'translation-mode');
         }
        }
      });

      this.initializeErrors();
  }

  async ngAfterViewInit() {
    await Task.delay(0);
    this.navigationSubscription = this.router.events
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.isLoading = true;
        }
        else if (
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel
        ) {
          this.isLoading = false;
        }
      });

    this.authService.init();
    this.appManagerService.init();
  }

  ngOnDestroy() {
    this.userInteractionSubscription.unsubscribe();
    this.navigationSubscription.unsubscribe();
    this.setupTranslationSystemService.translationMode$.unsubscribe();
    this.renderer.removeClass(document.body, 'translation-mode');
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }

  async stopImpersonation() {
    try {
      this.isLoading = true;
      await this.loginService.stopImpersonationAsync();      
    }
    finally {
      this.isLoading = false;
    }
  }

  async logout() {
    await this.loginService.logoutAsync(true);
  }

  async downloadHoah() {

    const toast = this.toastService.info('Noah Installer Download', null, { progressBar: true, progressAnimation: 'increasing', timeOut: 0, extendedTimeOut: 0, tapToDismiss: false, closeButton: false });

    try {
      const result = await this.http.getFileAsync("NoahClient", null, null, true, (l, t) => {
        const percentDone = Math.round(100 * l / t);

        toast.toastRef.componentInstance.width = percentDone;
      });

      result.save();
    }
    finally {
      toast.toastRef.close();
    }
  }

  openTrainingVideo() {

    const currentUser = this.authService.currentUserValue;

    if (currentUser && currentUser.systemPreferences && currentUser.systemPreferences.trainingMaterialUrl) {
      const win = window.open(currentUser.systemPreferences.trainingMaterialUrl, '_blank');
      win.focus();
    }
  }

  private initializeErrors()
  {
    this.errorService.getError()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(async () =>
      {        
        await this.modalService.showErrorModalAsync(x => x.Messages.MessageHeadersAndActions.SomethingHappened, x => x.Messages.ErrorMessages.ServerErrorOccurred);              
        //this.broadcastService.next({ name: BroadCastEvents.ServerErrorOccurred });
      });
    
    return false;
  }
}
